/**
 * Checks if a value is an object with a specific property.
 * This is a type guard.
 * @param value The value to check.
 * @param key The property key to check for.
 */
function hasProperty<K extends PropertyKey>(
  value: unknown,
  key: K,
): value is { [P in K]: unknown } {
  return typeof value === 'object' && value !== null && key in value;
}

/**
 * Extracts common properties from an unknown error object.
 * @param error The unknown error.
 * @returns An object with code and message properties.
 */
export function getErrorDetails(error: unknown): {
  code: string;
  message: string;
} {
  // Prioritize objects that look like our ApiError (duck-typing)
  if (hasProperty(error, 'status') && hasProperty(error, 'message')) {
    return {
      code: String(error.status ?? ''),
      message: String(error.message ?? ''),
    };
  }

  // Fallback for standard Error instances
  if (error instanceof Error) {
    return { code: '', message: error.message };
  }

  // Final fallback for any other type
  return { code: '', message: String(error ?? 'An unknown error occurred') };
}
